import { type Address, makeGoogleMaps, mapGeocodingResultToLocationProfile } from '.';

export type AutocompleteResult = { placeId: string; label: string };

export type RequestAutocomplete = (query: string, limit?: number) => Promise<AutocompleteResult[]>;
export type RequestAddressDetail = (address: { placeId: string }, onMissingPostalcode?: () => void) => Promise<Address>;

type Config = { language?: string; countryRestrictions: string[]; apiKey: string };

type CreateGoolgeMapsRequests = (config: Config) => {
  requestAutocomplete: RequestAutocomplete;
  requestAddressDetail: RequestAddressDetail;
};

export const makeGoogleMapsRequesters: CreateGoolgeMapsRequests = ({ language, countryRestrictions, apiKey }) => {
  const getGoogleMaps = makeGoogleMaps(apiKey);

  const requestAutocomplete: ReturnType<CreateGoolgeMapsRequests>['requestAutocomplete'] = async (query) => {
    const googleMaps = await getGoogleMaps();
    const service = new googleMaps.places.AutocompleteService();

    return service
      .getPlacePredictions({
        input: query,
        types: ['address'],
        componentRestrictions: { country: countryRestrictions },
        language,
      })
      .then((response) => {
        return response.predictions.map((prediction) => ({
          placeId: prediction.place_id,
          label: prediction.description,
        }));
      });
  };

  const requestAddressDetail: ReturnType<CreateGoolgeMapsRequests>['requestAddressDetail'] = async (
    { placeId },
    onMissingPostalcode,
  ): Promise<Address> => {
    const googleMaps = await getGoogleMaps();
    const geocoder = new googleMaps.Geocoder();
    const response = await geocoder.geocode({ placeId });
    const locationProfile = mapGeocodingResultToLocationProfile(response.results[0], onMissingPostalcode);
    const geoLocation = {
      latitude: String(response.results[0].geometry.location.lat()),
      longitude: String(response.results[0].geometry.location.lng()),
    };

    return {
      ...locationProfile,
      geoLocation,
    } as Address;
  };

  return {
    requestAutocomplete,
    requestAddressDetail,
  };
};
